import React, { useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import theme from 'theme'
import { useNavigate } from 'react-router-dom'
import { Box, Link as MuiLink } from '@mui/material'
import { mapLocation } from 'lib/carDetailsLib'
import { mapMessagesForDownload } from 'lib/messagesLib'
import { isWithinInterval } from 'date-fns'

import PageTitle from 'components/atoms/PageTitle'
import SearchField from 'components/atoms/SearchField'
import ConfirmBar from 'components/atoms/ConfirmBar'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import CarsList from 'components/atoms/CarsList'
import PageSessionSelection from 'components/atoms/PageSessionSelection'
import DownloadBar from 'components/atoms/DownloadBar'
import PagePartnersSelect from 'components/atoms/PagePartnersSelect'
import InaccessibleSession from 'components/atoms/InaccessibleSession'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput from 'hooks/useInputOutput'

import CarsBookedCarDetails from '../CarsBookedCarDetails'
import { getRepriceValues } from '../../../lib/repriceFee'

import { getCarsBookedUrl } from './carsBookedLib'
import CarBookedCarAdditionalDetails from './CarBookedCarAdditionalDetails'
import CarsBookedChoicesRecap from './CarsBookedChoicesRecap'

function getRowsForDownload(titlesRow, cars, t, session) {
  return [
    titlesRow,
    ...cars.map((car) => {
      return [
        `${car.title}`,
        car.price || '-',
        car.reprice || '-',
        car.km,
        mapLocation(car.location),
        t(`carDetails.mapping.${car.transmission}`),
        car.reconditioningEstCost || '-',
        car.year,
        car.fuelType,
        car.plate || '-',
        // car.vin || '-', // Uncomment if you want to include VIN in the download
        Math.round(car.optionAmount) || '-',
        t(`carsBooked.status.${car.assignedState}`),
        car.messages ? mapMessagesForDownload(car.messages, t) : '-',
      ]
    }),
  ]
}

const CarsBookedInaccessibleSession = ({
  hasAccessToSession,
  isInPublication,
}) => {
  const { t } = useTranslation()
  return (
    <InaccessibleSession hideAction>
      {!hasAccessToSession
        ? t('carsBooked.carsToBuyNoAccess')
        : isInPublication
        ? t('carsBooked.carsToBuyStillOpen')
        : t('carsBooked.carsToBuyNoChoices')}
    </InaccessibleSession>
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function CarsBooked({
  session,
  partnerId,
  partners,
  cars,
  sessions,
  currentActiveSession,
  hasAccessToSession,
  readOnly,
}) {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showDetails, setShowDetails] = useState()
  const [filteredCars, setFilteredCars] = useState([])

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setYearRange,
    setBrands,
    setModels,
    setFuels,
    setQuery,
    setCarsBookedType,
    filterCars,
    resetFilters,
  } = useCarsFilters()

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [cars, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const handleChangeSearch = (event) => {
    setQuery(event.target.value)
  }

  const carsContent = useMemo(
    () =>
      filteredCars.map((car, index) => {
        const repriceValues = car?.reprice
          ? getRepriceValues(car.reprice, car.feePercentage)
          : {}

        const priceNetToShow = car?.reprice
          ? repriceValues.repriceNet
          : car?.price
        const feeToShow = car?.reprice ? repriceValues.repriceFee : car?.fee

        return (
          <CarCard
            data-testid={`car-${index}`}
            key={car.originalId}
            height={theme.custom.carCardAdditionalMaxHeight}
            car={car}
            price={priceNetToShow}
            fee={feeToShow}
            inventoryPrice={car.inventoryPrice}
            inventoryFee={car.inventoryFee}
            hideTotalPrice
            onClick={() => setShowDetails(car.vin)}
            rightProps={{ justifyContent: 'flex-end' }}
            right={
              <>
                <MuiLink
                  color="primary"
                  data-testid="car-detail-right-click"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setShowDetails(car.vin)}
                  component={Box}
                  variant="body2"
                >
                  <strong>{t('carsBooked.details')}</strong>
                </MuiLink>
              </>
            }
            bottom={<CarBookedCarAdditionalDetails car={car} />}
          />
        )
      }),
    [filteredCars, setShowDetails, t]
  )

  const carFilters = useMemo(
    () => (
      <CarFiltersColumn
        filters={filters}
        filtersData={filtersData}
        filteredCars={
          filteredCars?.length !== cars.length ? filteredCars : undefined
        }
        setKmRange={setKmRange}
        setYearRange={setYearRange}
        setBrands={setBrands}
        setModels={setModels}
        setFuels={setFuels}
        resetFilters={resetFilters}
        readOnly={readOnly}
      />
    ),
    [
      filters,
      filtersData,
      filteredCars,
      cars.length,
      setKmRange,
      setYearRange,
      setBrands,
      setModels,
      setFuels,
      resetFilters,
      readOnly,
    ]
  )

  const isInPublication = useMemo(
    () =>
      isWithinInterval(new Date(), {
        start: new Date(session?.agenda?.publication?.startsAt),
        end: new Date(session?.agenda?.publication?.endsAt),
      }),
    [
      session?.agenda?.publication?.endsAt,
      session?.agenda?.publication?.startsAt,
    ]
  )

  const { saveXls } = useInputOutput()

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.name'),
      t('common.excel.price'),
      t('common.excel.reprice'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      // t('carDetails.vin'), // Uncomment if you want to include VIN in the download
      t('carDetails.optionals'),
      t('common.excel.status'),
      t('common.excel.messages'),
    ]

    const rows = getRowsForDownload(titlesRow, cars, t, session)

    saveXls(`cars-booked`, rows)
  }

  const handlePartnerChange = (event) => {
    navigate(getCarsBookedUrl(session?.sessionId, event.target.value))
  }

  const isSessionClosed = session?.agenda?.assignment?.state === 'closed'
  return showDetails ? (
    <CarsBookedCarDetails
      vin={showDetails}
      partnerId={partnerId}
      session={session}
      cars={filteredCars}
      isSessionClosed={isSessionClosed}
      setShowDetails={setShowDetails}
    />
  ) : (
    <>
      <PageTitle
        label={t('carsBooked.title')}
        rightColumn={
          <>
            <PageSessionSelection
              session={session}
              currentActiveSession={currentActiveSession}
              handleStepUrl={(sessionId) =>
                getCarsBookedUrl(sessionId, partnerId)
              }
              sessionsList={sessions}
              statusLabel={t('carsBooked.assignmentStatus')}
              task="assignment"
              readOnly={readOnly}
            />
            {partners.length > 0 && (
              <>
                <Box pr={2} />
                <PagePartnersSelect
                  partners={partners}
                  value={partnerId}
                  handleChange={handlePartnerChange}
                  readOnly={readOnly}
                />
              </>
            )}
          </>
        }
      />
      {!hasAccessToSession || !cars?.length || isInPublication ? (
        <CarsBookedInaccessibleSession
          hasAccessToSession={hasAccessToSession}
          isInPublication={isInPublication}
        />
      ) : (
        <>
          <ConfirmBar
            left={
              <SearchField
                placeholder={t('common.form.searchBy')}
                value={filters.query}
                onChange={handleChangeSearch}
                data-testid="text-search"
              />
            }
            leftProps={{ sx: { width: 0.5 } }}
            top={
              <CarsBookedChoicesRecap
                cars={cars}
                filters={filters}
                isSessionClosed={isSessionClosed}
                setFilteringRecapValues={setCarsBookedType}
              />
            }
            topProps={{
              sx: {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              },
            }}
            right={<DownloadBar handleDownload={handleDownload} />}
            hideConfirm
            sticky
          />
          <CarsList left={carFilters} right={carsContent} />
        </>
      )}
    </>
  )
}

CarsBooked.propTypes = {
  session: T.object.isRequired,
  partnerId: T.string,
  partners: T.array,
  cars: T.array,
  sessions: T.array,
  currentActiveSession: T.object,
  hasAccessToSession: T.bool,
  readOnly: T.bool,
}
